import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useContext, useState, useEffect } from "react";
import bg from "../@shared/assets/mydecksbg.png";
import { motion } from "framer-motion";
import { useAuthStore } from "../@shared/store/useAuthStore"; // ← Ensure this has a loading state
import { validateAuthInputs } from "../@shared/utils/Utils";
import { httpsCallable, getFunctions } from "firebase/functions";
import Toast from "../components/Toast";
import { MainContext } from "../App";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";
import {
  sendEmailVerification,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { auth, db } from "../@shared/firebase/config";
import { useNavigate } from "react-router-dom";
import { CTOS, IPTOS } from "../@shared/utils/TOS";
import confetti from "canvas-confetti";
import { countryCodes } from "../@shared/utils/countryCodes";

const OnboardingScreen = () => {
  const { login, signUp, userData } = useAuthStore();
  const { setError, setSuccess, setMessage, Theme } = useContext(MainContext);
  const [showSignIn, setShowSignIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stripeComplete, setStripeComplete] = useState(false);
  const [verificationClicked, setVerificationClicked] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [retryVisible, setRetryVisible] = useState(false);
  const [tosScrolled, setTosScrolled] = useState(false);
  const [country, setCountry] = useState("");

  const navigate = useNavigate();

  const [form, setForm] = useState({
    fullName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "", // "ip_provider" or "collection_creator"
  });

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleRoleChange = (role) => () => {
    setForm((prev) => ({
      ...prev,
      role: prev.role === role ? "" : role,
    }));
  };

  const whiteFieldStyle = {
    mb: 2,
    input: {
      color: "#000", // text color inside the input
    },
    label: {
      color: "#999", // optional: label color
    },
    backgroundColor: "#fff", // background color (normal)
    "&:hover": {
      backgroundColor: "#fff", // background color on hover
    },
    "& .MuiFilledInput-root": {
      backgroundColor: "#fff", // ensure root also stays white
      "&:hover": {
        backgroundColor: "#fff", // prevent grey on hover
      },
      "&:before": {
        borderBottomColor: "#ccc", // underline before focus
      },
      "&:hover:before": {
        borderBottomColor: "#ccc", // underline on hover
      },
    },
    // "& .MuiFilledInput-root": {
    //   backgroundColor: "#fff",
    //   borderRadius: 2,
    // },
    // "& .MuiInputBase-input": {
    //   color: "#000",
    // },
    // "& .MuiInputLabel-root": {
    //   color: "#444",
    // },
  };

  const handleSignIn = async () => {
    login(form.email, form.password);
  };

  const handleSubmit = async () => {
    const { fullName, username, email, password, confirmPassword, role } = form;

    if (
      !fullName ||
      !username ||
      !email ||
      !password ||
      !confirmPassword ||
      !role
    ) {
      setMessage("Please fill out all fields.");
      setError(true);
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      setError(true);
      return;
    }

    setLoading(true);

    const validFields = validateAuthInputs(form);
    const { status, message } = validFields;
    if (!status) {
      setMessage(message);
      setError(true);
      setLoading(false);
      return;
    }

    try {
      // 🔎 Check if username is already in use
      const usernameQuery = query(
        collection(db, "users"),
        where("username", "==", username)
      );
      const usernameSnapshot = await getDocs(usernameQuery);
      if (!usernameSnapshot.empty) {
        setMessage("Username already in use.");
        setError(true);
        return;
      }

      // 🔎 Check if email is already in use
      const emailQuery = query(
        collection(db, "users"),
        where("email", "==", email)
      );
      const emailSnapshot = await getDocs(emailQuery);
      if (!emailSnapshot.empty) {
        setMessage("Email already associated with another account.");
        setError(true);
        return;
      }

      // 🚀 Proceed with sign up
      await signUp(username, email, password, {
        name: fullName,
        role,
      });

      setSuccess(true);
      setMessage("Account created! Please check your email.");
    } catch (err) {
      console.error("Signup Error:", err.message);
      setMessage(err.message || "Something went wrong.");
      setError(true);
    }

    setLoading(false);
  };

  const handleSendVerification = async () => {
    try {
      await sendEmailVerification(auth.currentUser);
      setMessage("Verification email sent!");
      setSuccess(true);
      setVerificationClicked(true);
      setVerificationLoading(true);
      setRetryVisible(false);
    } catch (err) {
      setMessage(err.message || "Failed to send verification email.");
      setError(true);
    }
  };

  const handleStripeOnboard = async () => {
    try {
      setLoading(true);
      const functions = getFunctions();
      const createLink = httpsCallable(functions, "createStripeOnboardingLink");

      const userRef = doc(db, "users", userData.id);
      await updateDoc(userRef, {
        country: country,
      });

      const { data } = await createLink({
        country: country,
        origin: window.location.origin,
        test: window.location.origin.includes("localhost"), // remove in prod
      });

      window.location.href = data.url;
    } catch (err) {
      setMessage("Stripe onboarding failed.");
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAndSignOut = async () => {
    try {
      // 🎉 Launch confetti
      confetti({
        particleCount: 150,
        angle: 90,
        spread: 180,
        startVelocity: 45,
        origin: { y: 0.5 },
        zIndex: 9999,
      });

      if (userData?.id && !userData.onboardingComplete) {
        const userRef = doc(db, "users", userData.id);
        await updateDoc(userRef, {
          onboardingComplete: true,
          updatedAt: new Date(),
        });
      }

      setTimeout(async () => {
        await signOut(auth);
        navigate("/");
      }, 3500); // Give the confetti 1.5s before redirecting
    } catch (err) {
      console.error("Sign out error:", err.message);
      setMessage("Error signing out.");
      setError(true);
    }
  };

  useEffect(() => {
    if (!verificationClicked) return;

    let interval;
    let timeout;

    const startPolling = async () => {
      interval = setInterval(async () => {
        await auth.currentUser.reload();
        const verified = auth.currentUser.emailVerified;
        if (verified) {
          clearInterval(interval);
          clearTimeout(timeout);

          // Ideally also update your userData via Zustand or Firestore here
          setVerificationLoading(false);
          setVerificationClicked(false);
          setRetryVisible(false);
          setMessage("Email verified! Reloading...");
          setSuccess(true);

          // Optional: refetch userData from Firestore
          updateDoc(
            doc(db, "users", userData.id),
            {
              emailVerified: verified,
            },
            5000
          ); // or trigger re-auth/fetch user data logic
        }
      }, 5000);

      timeout = setTimeout(() => {
        clearInterval(interval);
        setVerificationLoading(false);
        setRetryVisible(true);
      }, 30000);
    };

    startPolling();

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [verificationClicked]);

  useEffect(() => {
    const verifyStripe = async () => {
      if (!userData?.stripeId) return;

      console.log("Checking...");
      try {
        const functions = getFunctions();
        const checkStatus = httpsCallable(
          functions,
          "checkStripeOnboardingStatus"
        );
        const res = await checkStatus({
          stripeId: userData.stripeId,
          test: window.location.origin.includes("localhost"),
        });

        const { charges_enabled, payouts_enabled, details_submitted } =
          res.data;

        if (details_submitted) {
          setStripeComplete(true);
        }
      } catch (err) {
        console.error("Stripe verification error:", err);
      }
    };

    verifyStripe();
  }, [userData?.stripeId]);

  return (
    <>
      <Grid
        container
        xs={12}
        sx={{
          height: "100%",
          width: "100%",
          backgroundImage: `url(${bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          overflowX: "hidden",
          position: "relative",
          justifyContent: "center",
          alignItems: "center",
          pt: 6,
          pb: 12,
        }}
      >
        <Toast onFinish={() => setLoading(false)} />
        <Box
          sx={{
            background: "#00000090",
            p: 4,
            borderRadius: 4,
            maxWidth: 480,
            width: "90%",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <Typography variant="h4" gutterBottom>
            Welcome to InfiNFT Royale
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {(!userData || (userData && !userData.emailVerified)) &&
              "Let’s get you set up as a creator or IP provider."}
            {userData &&
              userData.emailVerified &&
              !stripeComplete &&
              "Please read the TOS and select your country to continue."}
            {userData?.stripeId && stripeComplete && ""}
          </Typography>

          {!userData && (
            <>
              <Typography sx={{ color: "#fff", mb: 2 }}>
                Already started onboarding?
                <span
                  style={{
                    color: Theme.palette.primary.main,
                    cursor: "pointer",
                    textDecoration: "underline",
                    marginLeft: 5,
                  }}
                  onClick={() => setShowSignIn(true)}
                >
                  Sign back in to continue.
                </span>
              </Typography>

              {showSignIn ? (
                <>
                  <TextField
                    fullWidth
                    label="Email"
                    variant="filled"
                    sx={whiteFieldStyle}
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    variant="filled"
                    sx={whiteFieldStyle}
                    type="password"
                    value={form.password}
                    onChange={handleChange("password")}
                  />
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Full Name"
                    variant="filled"
                    sx={whiteFieldStyle}
                    value={form.fullName}
                    onChange={handleChange("fullName")}
                  />
                  <TextField
                    fullWidth
                    label="Username (Gamertag)"
                    variant="filled"
                    sx={whiteFieldStyle}
                    value={form.username}
                    onChange={handleChange("username")}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    variant="filled"
                    sx={whiteFieldStyle}
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    variant="filled"
                    sx={whiteFieldStyle}
                    type="password"
                    value={form.password}
                    onChange={handleChange("password")}
                  />
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    variant="filled"
                    sx={whiteFieldStyle}
                    type="password"
                    value={form.confirmPassword}
                    onChange={handleChange("confirmPassword")}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "start",
                      mb: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.role === "ip_provider"}
                          onChange={handleRoleChange("ip_provider")}
                          sx={{
                            color: "#fff",
                            "&.Mui-checked": {
                              color: "#6f52ed", // Highlighted when selected
                            },
                          }}
                        />
                      }
                      label="NFT Holder (IP Provider)"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.role === "collection_creator"}
                          onChange={handleRoleChange("collection_creator")}
                          sx={{
                            color: "#fff",
                            "&.Mui-checked": {
                              color: "#6f52ed", // Highlighted when selected
                            },
                          }}
                        />
                      }
                      label="Collection Creator"
                    />
                  </Box>
                </>
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  showSignIn ? handleSignIn() : handleSubmit();
                }}
                disabled={loading}
                sx={{
                  bgcolor: "#6f52ed",
                  "&:hover": { bgcolor: "#5a3ed3" },
                  fontWeight: 700,
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : showSignIn ? (
                  "Continue Onboarding"
                ) : (
                  "Create Account"
                )}
              </Button>
            </>
          )}
          {userData && !userData.emailVerified && (
            <Box
              sx={{
                mt: 4,
                backgroundColor: "#ffffff10",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "#fff", mb: 2 }}>
                Please verify your email address to continue.
              </Typography>

              {verificationLoading ? (
                <Typography sx={{ color: "#aaa", mb: 2 }}>
                  Waiting for verification...
                </Typography>
              ) : retryVisible ? (
                <Button
                  variant="outlined"
                  onClick={handleSendVerification}
                  sx={{ color: "#fff", borderColor: "#fff", mt: 1 }}
                >
                  Retry Verification
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSendVerification}
                  sx={{
                    bgcolor: "#6f52ed",
                    "&:hover": { bgcolor: "#5a3ed3" },
                    fontWeight: 700,
                  }}
                >
                  Send Verification Email
                </Button>
              )}
            </Box>
          )}

          {userData && userData.emailVerified && !stripeComplete && (
            <>
              <Box
                sx={{
                  height: 200,
                  overflowY: "auto",
                  backgroundColor: "#ffffff10",
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.target;
                  if (scrollTop + clientHeight >= scrollHeight - 10) {
                    setTosScrolled(true);
                  }
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: 14,
                    lineHeight: 1.5,
                    whiteSpace: "pre-line",
                  }}
                >
                  {userData.roles.includes("ip_provider") ? IPTOS : CTOS}
                </Typography>
              </Box>

              {/* Country Selector */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="country-label" sx={{ color: "#fff" }}>
                  Select Country
                </InputLabel>
                <Select
                  labelId="country-label"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  sx={{
                    position: "relative",
                    top: 6,
                    color: "#fff",
                    ".MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6f52ed",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6f52ed",
                    },
                  }}
                >
                  {countryCodes.map(({ country, code, flag }) => (
                    <MenuItem key={code} value={code}>
                      {flag} {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                fullWidth
                variant="contained"
                disabled={!tosScrolled || loading || !country}
                onClick={handleStripeOnboard}
                sx={{
                  bgcolor: "#6f52ed",
                  "&:hover": { bgcolor: "#5a3ed3" },
                  fontWeight: 700,
                }}
              >
                {loading ? "Loading..." : "Complete Onboarding"}
              </Button>
            </>
          )}

          {userData?.stripeId && stripeComplete && (
            <Box
              sx={{
                mt: 4,
                backgroundColor: "#ffffff10",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{ color: "#fff", mb: 2, fontWeight: "bold" }}
              >
                🎉 You're Officially Onboarded!
              </Typography>

              <Typography sx={{ color: "#ddd", fontSize: 15, mb: 2 }}>
                Thank you for completing the onboarding process. You're now part
                of the foundation that will define the world of InfiNFT Royale!
              </Typography>

              <Typography sx={{ color: "#ddd", fontSize: 15, mb: 2 }}>
                Our team will reach out soon to confirm your submission and
                review your character cards. Sit tight — great things are
                coming!
              </Typography>

              <Typography sx={{ color: "#ddd", fontSize: 15, mb: 2 }}>
                Season 1 is just around the corner. Your early involvement helps
                shape the game's legacy and boosts the visibility and impact of
                your NFTs over time!
              </Typography>

              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: "#6f52ed",
                  "&:hover": { bgcolor: "#5a3ed3" },
                  fontWeight: 700,
                  mt: 3,
                }}
                onClick={handleConfirmAndSignOut}
              >
                Confirm & Sign Out
              </Button>
            </Box>
          )}
        </Box>
      </Grid>
      <motion.footer
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          width: "100%",
          backgroundColor: "#0e0326",
          padding: "3rem 1.5rem",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <img
          src={require("../@shared/assets/logo-icon-text-reg.png")}
          alt="Logo"
          style={{ height: 75, marginBottom: "1rem" }}
        />

        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            justifyContent: "center",
            mb: 2,
          }}
        >
          {["About", "Roadmap", "FAQ", "Support", "Terms"].map((label) => (
            <Typography
              key={label}
              sx={{
                fontSize: 14,
                color: "#aaa",
                cursor: "pointer",
                "&:hover": {
                  color: "#fff",
                },
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>

        <Typography variant="body2" sx={{ fontSize: 12, color: "#888", mt: 2 }}>
          © {new Date().getFullYear()} InfiNFT Royale. All rights reserved.
        </Typography>
      </motion.footer>
    </>
  );
};

export default OnboardingScreen;
